import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import SeedEditorial from "@/components/SeedEditorial";
import ContactCTA from "@/components/ContactCTA";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seo";
import { DB } from "@/lib/media";

const ease = [0.22, 1, 0.36, 1];

const VALUES = [
    {
        no: "01",
        title: "Small on purpose",
        body: "We stay boutique because proximity is the point. Fewer clients, deeper thinking, senior hands on every deliverable.",
    },
    {
        no: "02",
        title: "Craft over volume",
        body: "One well-thought identity, one honest campaign, one considered website — beats a thousand cookie-cutter templates every time.",
    },
    {
        no: "03",
        title: "Tied to outcomes",
        body: "We stop caring about the deliverable when it stops caring about the business. Every output has a number attached to it.",
    },
    {
        no: "04",
        title: "Long horizon",
        body: "Seeds don't grow overnight. We build for compound outcomes — years of brand equity, not quarters of paid traffic.",
    },
];

const WORK_ON = [
    { label: "Brand strategy & identity" },
    { label: "Websites & e-commerce" },
    { label: "AI creative production" },
    { label: "Performance marketing" },
    { label: "Content & always-on social" },
    { label: "Quick commerce & marketplaces" },
    { label: "Launch campaigns" },
    { label: "Growth sprints" },
];

export default function About() {
    return (
        <>
            <Seo {...SEO.about} />
            <PageHero
                data-testid="about-page-hero"
                kicker="/ About · 03"
                meta="Est. Mumbai · Founder-led"
                title="The studio"
                accent="behind it all"
                tagline="Digitalbeej is small, senior and founder-led — a modern creative studio that shows up like a partner, not a vendor."
            />

            {/* Studio image + intro */}
            <section
                data-testid="about-intro"
                className="relative py-16 lg:py-24"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-y-12 lg:gap-x-12">
                    <div className="col-span-12 lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, scale: 1.03 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.1, ease }}
                            className="relative overflow-hidden aspect-[5/4] bg-[#F5EFE1]"
                        >
                            <img
                                src={DB.teamAbout}
                                alt="Inside the Digitalbeej studio, Mumbai"
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                            />
                            <div className="absolute top-4 left-4 text-bone text-[10px] uppercase tracking-[0.2em] font-bold mix-blend-difference">
                                The studio · Mumbai
                            </div>
                        </motion.div>
                    </div>

                    <div className="col-span-12 lg:col-span-5 lg:pl-6">
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, ease }}
                            data-testid="about-intro-heading"
                            className="font-display font-black uppercase tracking-tight leading-[0.92] text-4xl sm:text-5xl"
                        >
                            Building brands from{" "}
                            <span className="font-serif italic font-medium normal-case lowercase text-olive">
                                seed to scale.
                            </span>
                        </motion.h2>

                        <p className="mt-8 text-lg text-black/75 leading-relaxed">
                            Digitalbeej is a modern creative studio that
                            combines branding, websites, AI, performance
                            marketing and commerce growth to help ambitious
                            businesses grow with clarity and purpose.
                        </p>

                        <p className="mt-5 text-black/70 leading-relaxed">
                            We&apos;re founder-led on purpose. Every brief goes
                            through senior thinking, every deliverable is
                            reviewed by hands that have shipped hundreds of
                            them, and every engagement is small enough to
                            actually care about.
                        </p>

                        {/* Founder credit */}
                        <div
                            data-testid="about-founder"
                            className="mt-10 pt-8 border-t border-black/15 flex items-start gap-5"
                        >
                            <div className="w-14 h-14 rounded-full bg-olive/15 border border-olive/40 flex items-center justify-center font-display font-black text-lg text-[#2E5019]">
                                AS
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55">
                                    Founder
                                </div>
                                <div className="mt-1 font-display font-bold text-xl tracking-tight">
                                    Abhay Singh
                                </div>
                                <div className="text-sm text-black/60">
                                    Founder &amp; Growth Strategist
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Philosophy (reused editorial) */}
            <SeedEditorial />

            {/* Values */}
            <section
                data-testid="about-values"
                className="bg-obsidian text-bone relative py-24 lg:py-32 grain"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
                    <div className="text-xs uppercase tracking-[0.22em] font-bold text-bone/50">
                        (Values / 04)
                    </div>
                    <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
                        How we{" "}
                        <span className="dark-outline-text">show up.</span>
                    </h2>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 border-t border-bone/15">
                        {VALUES.map((v, i) => (
                            <motion.div
                                key={v.no}
                                data-testid={`about-value-${v.no}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{
                                    duration: 0.9,
                                    ease,
                                    delay: (i % 2) * 0.1,
                                }}
                                className={`py-10 lg:py-14 lg:pr-8 lg:pl-8 first:lg:pl-0 border-bone/15 border-b ${
                                    i % 2 === 0 ? "md:border-r" : ""
                                } ${i > 1 ? "md:border-b-0" : ""}`}
                            >
                                <div className="font-display font-black text-4xl text-olive mb-4">
                                    {v.no}
                                </div>
                                <h3 className="font-display font-bold text-2xl lg:text-3xl tracking-tight">
                                    {v.title}
                                </h3>
                                <p className="mt-4 text-bone/75 leading-relaxed max-w-md">
                                    {v.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* What we work on */}
            <section
                data-testid="about-work-on"
                className="relative py-24 lg:py-32"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <div className="grid grid-cols-12 gap-y-10 lg:gap-x-12">
                        <div className="col-span-12 lg:col-span-5">
                            <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                                (What we work on / 05)
                            </div>
                            <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-4xl sm:text-5xl">
                                Founders, SMEs, D2C brands and everything in
                                between.
                            </h2>
                            <p className="mt-6 font-serif italic text-xl lg:text-2xl text-black/60 max-w-md">
                                We&apos;re sector-flexible but taste-consistent.
                            </p>
                        </div>

                        <div className="col-span-12 lg:col-span-7">
                            <ul className="grid grid-cols-1 sm:grid-cols-2 border-t border-black/15">
                                {WORK_ON.map((w, i) => (
                                    <motion.li
                                        key={w.label}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{
                                            once: true,
                                            margin: "-60px",
                                        }}
                                        transition={{
                                            duration: 0.7,
                                            ease,
                                            delay: (i % 4) * 0.06,
                                        }}
                                        className={`py-5 flex items-center gap-4 border-b border-black/15 ${
                                            i % 2 === 0 ? "sm:pr-6" : "sm:pl-6"
                                        }`}
                                    >
                                        <span className="font-display font-black text-olive w-8 shrink-0">
                                            0{i + 1}
                                        </span>
                                        <span className="font-display font-bold text-lg tracking-tight">
                                            {w.label}
                                        </span>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <ContactCTA
                kicker="Curious to see if we fit?"
                title="One conversation is all it takes."
                body="Start on WhatsApp — no forms, no funnels, no fluff. Just a real conversation about where your brand is."
            />
        </>
    );
}
